import { create } from 'zustand';
import { GroupMemberTagData } from '../dto/EntityRepository';
import type { ResourceScope, ResourceStatus } from '../util/scopedResource';

const sortTagsLikeServer = (tags: GroupMemberTagData[]): GroupMemberTagData[] =>
  [...tags].sort((a, b) => {
    const nameCompare = a.name.localeCompare(b.name, 'ko-KR');
    if (nameCompare !== 0) return nameCompare;
    if (a.index !== b.index) return a.index - b.index;
    return a.id - b.id;
  });

const isSameTagList = (
  prev: GroupMemberTagData[],
  next: GroupMemberTagData[],
): boolean =>
  prev.length === next.length &&
  prev.every((tag, index) => {
    const other = next[index];
    return tag.id === other.id && tag.name === other.name && tag.index === other.index;
  });

interface GroupMemberTagState {
  tags: GroupMemberTagData[];
  selectedTags: GroupMemberTagData[];
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
  beginGroupMemberTagListFetch: (ownerAui: string, scope: ResourceScope) => void;
  setTags: (
    tags: GroupMemberTagData[],
    ownerAui: string,
    scope: ResourceScope,
  ) => void;
  setGroupMemberTagListError: () => void;
  afterCreateTag: (tag: GroupMemberTagData) => void;
  afterUpdateTag: (tag: GroupMemberTagData) => void;
  afterDeleteTag: (tagId: number) => void;
  addSelectedTag: (tag: GroupMemberTagData) => void;
  removeSelectedTag: (tag: GroupMemberTagData) => void;
  clearGroupMemberTags: () => void;
}

export const useGroupMemberTagStore = create<GroupMemberTagState>((set) => ({
  tags: [],
  selectedTags: [],
  listOwnerAui: null,
  listScope: null,
  status: 'idle',
  beginGroupMemberTagListFetch: (ownerAui, scope) => set((state) => {
    // 같은 owner+scope 재fetch: success 유지(SWR). 다르면 loading으로 숨김.
    if (
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      state.status === 'success'
    ) {
      return state;
    }
    return { status: 'loading' };
  }),
  setTags: (tags, ownerAui, scope) => set((state) => {
    const sorted = sortTagsLikeServer(tags);
    const nextSelected = state.selectedTags.filter((selected) =>
      sorted.some((tag) => tag.id === selected.id),
    );
    if (
      state.status === 'success' &&
      state.listOwnerAui === ownerAui &&
      state.listScope === scope &&
      isSameTagList(state.tags, sorted) &&
      isSameTagList(state.selectedTags, nextSelected)
    ) {
      return state;
    }
    return {
      tags: sorted,
      selectedTags: nextSelected,
      listOwnerAui: ownerAui,
      listScope: scope,
      status: 'success',
    };
  }),
  setGroupMemberTagListError: () => set({ status: 'error' }),
  afterCreateTag: (tag) => set(({ tags }) => ({
    tags: sortTagsLikeServer([...tags, tag]),
  })),
  afterUpdateTag: (tag) => set(({ tags, selectedTags }) => ({
    tags: sortTagsLikeServer(
      tags.map((item) => item.id === tag.id ? tag : item),
    ),
    selectedTags: selectedTags.map((item) => item.id === tag.id ? tag : item),
  })),
  afterDeleteTag: (tagId) => set(({ tags, selectedTags }) => ({
    tags: tags.filter((item) => item.id !== tagId),
    selectedTags: selectedTags.filter((item) => item.id !== tagId),
  })),
  addSelectedTag: (tag) => set(({ selectedTags }) => ({
    selectedTags: selectedTags.some((selected) => selected.id === tag.id)
      ? selectedTags
      : [...selectedTags, tag],
  })),
  removeSelectedTag: (tag) => set(({ selectedTags }) => ({
    selectedTags: selectedTags.filter((selected) => selected.id !== tag.id),
  })),
  clearGroupMemberTags: () => set((state) => {
    if (
      state.tags.length === 0 &&
      state.selectedTags.length === 0 &&
      state.listOwnerAui === null &&
      state.listScope === null &&
      state.status === 'idle'
    ) {
      return state;
    }
    return {
      tags: [],
      selectedTags: [],
      listOwnerAui: null,
      listScope: null,
      status: 'idle',
    };
  }),
}));
